<i18n>
{
  "de": {
    "topics": "Themen",
    "filters": "Filter",
    "advancedFilters": "Erweiterte Filter",
    "visibility": "Sichtbarkeit",
    "showOnlyPublicPosts": "Nur öffentliche Beiträge anzeigen",
    "showOnlyFollowerPosts": "Nur Beiträge für Follower anzeigen",
    "resetFilters": "Filter zurücksetzen"
  },
  "en": {
    "topics": "Topics",
    "filters": "Filters",
    "advancedFilters": "Advanced Filters",
    "visibility": "Visibility",
    "showOnlyPublicPosts": "Show only public posts",
    "showOnlyFollowerPosts": "Show only posts for followers",
    "resetFilters": "Reset filters"
  }
}
</i18n>

<template>
  <div
    v-if="artist && !isMobileView"
    class="filter-posts on-body-background"
  >
    <!-- Topics Section -->
    <div
      v-if="postTags && postTags.length"
      class="filter-section"
    >
      <h4 class="filter-section__title">
        <i class="icon-tags" />
        {{ $t('topics') }}
      </h4>
      <div class="filter-chips">
        <nuxt-link
          v-for="tag of postTags"
          :key="tag._id"
          :to="localePath({
            name: tagIds.includes(tag.id) ? 'id-posts' : 'id-posts-tag',
            params: tagIds.includes(tag.id) ? { id: artist.urlAlias } : { id: artist.urlAlias, tag: tag.slug },
          })"
          class="filter-chip"
          :class="{ 'filter-chip--active': tagIds.includes(tag.id) }"
        >
          {{ tag.name }}
        </nuxt-link>
      </div>
    </div>

    <!-- Mobile filter toggle button -->
    <button
      v-if="isMobileView && !filterVisible"
      class="filter-posts__mobile-toggle"
      @click="showFilters"
    >
      <i class="icon-filter" />
      {{ $t('filters') }}
    </button>

    <div v-show="!isMobileView || filterVisible">
      <!-- Advanced Filters Section -->
      <div class="filter-section">
        <h4 class="filter-section__title">
          <i class="icon-sliders" />
          {{ $t('advancedFilters') }}
        </h4>
        <div class="multi-select-filter">
          <filterPostsByReward
            :layout="layout"
            :clear="clearFilterByReward"
            @filter="handleFilter"
          />
          <filterPostsByMonth
            :layout="layout"
            @filter="handleFilter"
          />
          <filterPostsByContent
            :layout="layout"
            @filter="handleFilter"
          />
        </div>
      </div>

      <!-- Visibility Section -->
      <div class="filter-section">
        <h4 class="filter-section__title">
          <i class="icon-eye" />
          {{ $t('visibility') }}
        </h4>
        <div class="visibility-options">
          <label class="visibility-option">
            <input
              id="show-public-posts"
              type="checkbox"
              class="visibility-option__checkbox"
              name="show-public-posts"
              :checked="filter.public"
              @change="showOnlyPublicPosts"
            >
            <span class="visibility-option__checkmark" />
            <span class="visibility-option__text">{{ $t('showOnlyPublicPosts') }}</span>
          </label>

          <label class="visibility-option">
            <input
              id="show-follower-posts"
              type="checkbox"
              class="visibility-option__checkbox"
              name="show-follower-posts"
              :checked="filter.limitToFollowers"
              @change="showOnlyFollowerPosts"
            >
            <span class="visibility-option__checkmark" />
            <span class="visibility-option__text">{{ $t('showOnlyFollowerPosts') }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Reset Filters Button -->
    <button
      v-if="filtersActive"
      class="filter-posts__reset-btn"
      @click="resetFilters"
    >
      <i class="icon-times" />
      {{ $t('resetFilters') }}
    </button>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import filterPostsByReward from '~/components/filter-posts-by-rewards'
import filterPostsByMonth from '~/components/filter-posts-by-month'
import filterPostsByContent from '~/components/filter-posts-by-content'

export default {
  props: ['layout'],
  components: {
    filterPostsByReward,
    filterPostsByMonth,
    filterPostsByContent
  },
  data: () => ({
    filter: {
      tags: [],
      product: [],
      reward: [],
      months: [],
      contents: [],
      public: false,
      limitToFollowers: false
    },
    filterVisible: false
  }),
  computed: {
    filtersActive() {
      return this.postFilter.tags.length +
        this.postFilter.product.length +
        this.postFilter.reward.length +
        this.postFilter.months.length +
        this.postFilter.contents.length > 0 ||
        this.postFilter.public ||
        this.postFilter.limitToFollowers
    },
    clearFilterByReward() {
      return !this.filter.public && !this.filter.limitToFollowers
    },
    tagIds() {
      if (!this.postFilter) return []
      return this.postFilter.tags.map((t) => t.id)
    },
    ...mapState(['artist', 'profilePostsLoaded', 'profilePostsLoading', 'postFilter', 'postTags'])
  },
  watch: {
    layout: {
      handler(newLayout, oldLayout) {
        if (oldLayout && newLayout !== oldLayout) {
          // Reset mobile-specific state when layout changes
          this.filterVisible = false
        }
      },
      immediate: false
    }
  },

  methods: {
    resetFilters() {
      // First emit the reset event to child components before clearing state
      this.$root.$emit('resetAllFilters')

      this.filter = {
        tags: [],
        product: [],
        reward: [],
        months: [],
        contents: [],
        public: false,
        limitToFollowers: false
      }
      this.resetPostFilter()
      this.filterVisible = false
      this.$router
        .push(this.localePath({ name: 'id-posts', params: { id: this.artist.urlAlias } }))
        .catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
    },

    showFilters() {
      this.filterVisible = !this.filterVisible
    },

    showOnlyPublicPosts(event) {
      this.filter.public = event.target.checked
      this.filter.limitToFollowers = false
      this.filter.product = []
      this.filter.reward = []
      this.executeFilter()
    },

    showOnlyFollowerPosts(event) {
      this.filter.limitToFollowers = event.target.checked
      this.filter.public = false
      this.filter.product = []
      this.filter.reward = []
      this.executeFilter()
    },
    handleFilter(filter) {
      this.filter = this.postFilter
      if (
        (filter.product || []).length ||
        (filter.reward || []).length
      ) {
        this.filter.public = false
        this.filter.limitToFollowers = false
      }

      Object.keys(filter)
        .filter((key) => !['public', 'limitToFollowers'].includes(filter[key]))
        .forEach((key) => {
          this.filter[key] = filter[key]
        })

      this.executeFilter()
    },
    async executeFilter() {
      if (!this.filter) return
      this.setPostFilter({ filter: this.filter })
      if (!this.profilePostsLoading && !this.profilePostsLoaded) {
        await this.loadAllPosts({
          urlAlias: this.artist.urlAlias
        })
      }
    },
    ...mapActions(['loadAllPosts', 'setPostFilter', 'resetPostFilter'])
  },
  async mounted() {
    this.filter = Object.assign({}, this.postFilter)
    this.executeFilter()
  },

  beforeDestroy() {
    // Clean up any lingering state when component is destroyed
    this.filterVisible = false
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/sass/_variables';

.filter-posts {
  padding: 15px 0;
}

// Section structure
.filter-section {
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 10px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1rem;
    font-weight: 600;
    color: var(--body-background-text-color, #333);
    margin: 0 0 15px 0;

    i {
      color: var(--body-background-text-color, #333);
      font-size: 14px;
    }
  }
}

// Filter chips (topics)
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  text-decoration: none;

  // Default state - semi-transparent with text color
  background: color-mix(
    in hsl,
    var(--body-background-color, #fff),
    var(--body-background-text-color, #333) 8%
  );
  color: var(--body-background-text-color, #333);

  // Subtle inner glow effect
  box-shadow:
    inset 0 0 0 1px color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 15%
    );

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      inset 0 0 0 1px color-mix(
        in hsl,
        var(--body-background-color, #fff),
        var(--body-background-text-color, #333) 25%
      ),
      0 4px 12px color-mix(
        in hsl,
        var(--body-background-color, #fff),
        black 15%
      );
  }

  // Active state - filled with primary color
  &--active {
    background: var(--primary-color, #fb3e52);
    color: var(--primary-text-color, #fff);
    box-shadow:
      0 4px 15px color-mix(
        in hsl,
        var(--primary-color, #fb3e52),
        transparent 50%
      );

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 6px 20px color-mix(
          in hsl,
          var(--primary-color, #fb3e52),
          transparent 40%
        );
    }
  }
}

// Visibility options (checkboxes)
.visibility-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.visibility-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-left: 35px;

  &__checkbox {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  &__checkmark {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 20px;
    width: 20px;
    background-color: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 5%
    );
    border: 2px solid color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 30%
    );
    border-radius: 4px;
    transition: all 0.2s ease;

    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 6px;
      top: 2px;
      width: 5px;
      height: 10px;
      border: solid var(--primary-text-color, #fff);
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  &__text {
    font-size: 0.9rem;
    color: var(--body-background-text-color, #333);
    line-height: 1.4;
  }

  &:hover .visibility-option__checkmark {
    background-color: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 10%
    );
    border-color: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 40%
    );
  }

  .visibility-option__checkbox:checked ~ .visibility-option__checkmark {
    background-color: var(--primary-color, #fb3e52);
    border-color: var(--primary-color, #fb3e52);

    &:after {
      display: block;
    }
  }
}

// Reset button - text link style
.filter-posts__reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  padding: 8px 0;
  background: transparent;
  border: none;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--body-background-text-color, #333);
  opacity: 0.7;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  i {
    font-size: 0.9rem;
  }
}

// Mobile toggle button
.filter-posts__mobile-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: color-mix(
    in hsl,
    var(--body-background-color, #fff),
    var(--body-background-text-color, #333) 8%
  );
  color: var(--body-background-text-color, #333);
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 15%
    );
  }

  i {
    font-size: 14px;
  }
}
</style>

<style lang="scss">
@import 'assets/sass/_variables';

// Multi-select filter container
.multi-select-filter {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

// Multi-select component styling
.multi-select {
  width: auto !important;
  min-width: 150px;
  position: relative;

  // Remove default arrow
  &::after {
    border: none !important;
  }

  // Main button styling - matches filter chip design
  > button,
  .btn-select {
    width: 100% !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    padding: 10px 20px !important;
    font-size: 0.9rem !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    border: none !important;
    border-radius: 5px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    white-space: nowrap !important;

    // Default state - semi-transparent with text color
    background: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 8%
    ) !important;
    color: var(--body-background-text-color, #333) !important;

    // Subtle inner glow effect
    box-shadow:
      inset 0 0 0 1px color-mix(
        in hsl,
        var(--body-background-color, #fff),
        var(--body-background-text-color, #333) 15%
      ) !important;

    .buttonLabel {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      width: 100% !important;
      color: inherit !important;

      .caret {
        border-top-color: var(--body-background-text-color, #333) !important;
        transition: border-color 0.2s ease !important;
      }
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        inset 0 0 0 1px color-mix(
          in hsl,
          var(--body-background-color, #fff),
          var(--body-background-text-color, #333) 25%
        ),
        0 4px 12px color-mix(
          in hsl,
          var(--body-background-color, #fff),
          black 15%
        ) !important;
    }
  }

  // Active selection state - filled with primary color
  &.has-selections {
    > button,
    .btn-select {
      background: var(--primary-color, #fb3e52) !important;
      color: var(--primary-text-color, #fff) !important;
      box-shadow:
        0 4px 15px color-mix(
          in hsl,
          var(--primary-color, #fb3e52),
          transparent 50%
        ) !important;

      .buttonLabel {
        color: var(--primary-text-color, #fff) !important;
        font-weight: 600 !important;
      }

      .caret {
        border-top-color: var(--primary-text-color, #fff) !important;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow:
          0 6px 20px color-mix(
            in hsl,
            var(--primary-color, #fb3e52),
            transparent 40%
          ) !important;
      }
    }
  }
  .checkBoxContainer {
    min-height: 0px !important;
  }
  // Dropdown panel styling
  .checkboxLayer {
    max-height: 400px !important;
    max-width: 300px !important;
    border-radius: 15px !important;
    border: none !important;
    overflow: hidden !important;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15) !important;
    background-color: var(--body-background-color, #fff) !important;

    // Items list
    li {
      margin-left: 0 !important;
      border-bottom: none !important;
      transition: background-color 0.15s ease !important;

      &:hover {
        background-color: color-mix(
          in hsl,
          var(--body-background-color, #fff),
          var(--body-background-text-color, #333) 5%
        ) !important;
      }

      span {
        margin-left: 0px !important;
        white-space: normal;
        font-size: 0.85rem !important;
        color: var(--body-background-text-color, #333) !important;
      }
    }
  }
}

// Selected item styling (the actual options in the dropdown)
.selectItem {
  padding: 6px 12px !important;
  cursor: pointer !important;

  &.active {
    background-color: color-mix(
      in hsl,
      var(--primary-color, #fb3e52),
      transparent 90%
    ) !important;
    color: var(--body-background-text-color, #333) !important;
    font-weight: 600 !important;
  }
}

// Tab container styling
.tab-block {
  padding: 6px 8px !important;
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: center !important;
  gap: 2px !important;
  border-bottom: 1px solid color-mix(
    in hsl,
    var(--body-background-color, #fff),
    var(--body-background-text-color, #333) 10%
  ) !important;
}

// Group tabs styling
.tab-item {
  margin: 0 !important;
  padding: 2px 5px !important;
  border-radius: 4px !important;
  transition: all 0.15s ease !important;
  background: transparent !important;

  &:hover:not(.active) {
    background-color: color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 5%
    ) !important;
  }

  &.active {
    background-color: color-mix(
      in hsl,
      var(--primary-color, #fb3e52),
      transparent 85%
    ) !important;

    span {
      color: var(--body-background-text-color, #333) !important;
      border-bottom: none !important;
      font-weight: 600 !important;
    }
  }

  span {
    font-size: 0.75rem !important;
    border-bottom: none !important;
  }
}

// Helper buttons (select all, etc.)
.helperButton {
  padding: 8px 12px !important;
  font-size: 0.8rem !important;
  color: var(--tiles-background-text-color, #fb3e52) !important;
  background: transparent !important;
  border: none !important;
  cursor: pointer !important;
  transition: opacity 0.2s ease !important;

  &:hover {
    opacity: 0.7 !important;
  }
}

@media only screen and (min-width: $mobile) {
  .helperButton {
    font-size: 0.75rem !important;
    color: var(--tiles-background-text-color, #fb3e52) !important;
  }
}

// Mobile responsive styles
@media only screen and (max-width: $mobile) {
  .filter-content-mobile,
  .filter-reward-mobile,
  .filter-month-mobile {
    font-size: 0.75rem !important;

    .helperButton {
      font-size: 0.75rem !important;
      color: var(--body-background-text-color, #fb3e52) !important;
    }

    .btn-select {
      text-align: left !important;
      padding: 8px 15px !important;

      .buttonLabel {
        span {
          font-size: 0.75rem;
        }
      }
    }
  }
}
</style>
